import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { UserProfile, WalletDTO } from '@/domain'

interface UserAuthenticated extends UserProfile {
  fullName: string
  hasIdentityVerified: boolean
  referral: { code: string }
}

interface UserState {
  userAuthenticated: UserAuthenticated | null
  wallet: WalletDTO | null
  isProfileLoaded: boolean
  setUserData: (data: { userAuthenticated?: UserAuthenticated }) => void
  setUserAuthenticated: (user: UserAuthenticated) => void
  setWallet: (wallet: WalletDTO) => void
  initializeMockWallet: () => void
  clearUserData: () => void
}

export const useUserStore = create<UserState>()(
  persist(
    (set) => ({
      userAuthenticated: null,
      wallet: null,
      isProfileLoaded: false,

      setUserData: (data) => {
        set((state) => ({
          ...state,
          ...data,
          isProfileLoaded: true,
        }))
      },

      setUserAuthenticated: (userAuthenticated) => {
        set((state) => ({
          ...state,
          userAuthenticated,
          isProfileLoaded: true,
        }))
      },

      setWallet: (wallet) => set({ wallet }),

      initializeMockWallet: () => {
        const mockWallet: WalletDTO = {
          walletId: 'mock-wallet-1',
          displayCurrency: 'USD',
          totalBalance: 2468.12,
          completeCurrencies: [
            {
              id: '1',
              code: 'USDT',
              name: 'Tether',
              amount: 1234.56,
              amountWithoutNumberConversion: '1,234.56',
              icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/usdt.png',
              label: '$',
              fiatAmount: 1234.56,
              fiatCurrency: 'USD',
              percentageChange24h: 0,
              revenue24hFiatAmount: 0,
              pricePerUnit: 1,
              ceilingValueForValidation: 10000,
              availableOnRampPaymentMethods: ['card'],
              formattedFiatBalance: '$1,234.56',
              formattedAmountBalance: '1,234.56 USDT',
              formattedUSDBalance: '$1,234.56',
              formattedStablecoinBalance: '1,234.56 USDT',
              usdBalance: 1234.56,
              displayCurrencyCode: 'USDT',
              symbol: '$',
              avatarBGRefColor: '#33CC67',
            },
            {
              id: '2',
              code: 'BTC',
              name: 'Bitcoin',
              amount: 0.02857142,
              amountWithoutNumberConversion: '0.02857142',
              icon: 'https://storage.googleapis.com/kem-catalog-stage/currencies/btc.png',
              label: '₿',
              fiatAmount: 1233.56,
              fiatCurrency: 'USD',
              percentageChange24h: 2.5,
              revenue24hFiatAmount: 30.84,
              pricePerUnit: 43000,
              ceilingValueForValidation: 300,
              availableOnRampPaymentMethods: ['card'],
              formattedFiatBalance: '$1,233.56',
              formattedAmountBalance: '0.02857142 BTC',
              formattedUSDBalance: '$1,233.56',
              formattedStablecoinBalance: '1,233.56 USDT',
              usdBalance: 1233.56,
              displayCurrencyCode: 'BTC',
              symbol: '₿',
              avatarBGRefColor: '#F7931A',
            },
          ],
          cards: [],
        }
        set({ wallet: mockWallet })
      },

      clearUserData: () => {
        set({
          userAuthenticated: null,
          wallet: null,
          isProfileLoaded: false,
        })
      },
    }),
    {
      name: 'user-store',
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name)
          return str ? JSON.parse(str) : null
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value))
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    },
  ),
)
